import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Lead = Database["public"]["Tables"]["lp_leads"]["Row"];

const formatoData = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const Route = createFileRoute("/admin/contatos")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Contatos | Painel" },
      { name: "description", content: "Mensagens recebidas pelo formulário da página de vendas." },
      { property: "og:title", content: "Contatos | Painel" },
      { property: "og:description", content: "Mensagens recebidas pelo formulário da página de vendas." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Contatos,
});

function Contatos() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from("lp_leads")
      .select("*")
      .order("criado_em", { ascending: false })
      .then(({ data, error }) => {
        setLeads(data ?? []);
        setErro(error ? "Não foi possível carregar os contatos." : null);
        setCarregando(false);
      });
  }, []);

  return (
    <div className="admin-surface">
      <header className="admin-cabecalho">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Contatos</h1>
          <p className="text-sm text-muted-foreground">
            Mensagens enviadas pelo formulário da página de vendas, mais recentes primeiro.
          </p>
        </div>
      </header>

      {carregando ? <p className="text-sm text-muted-foreground">Carregando...</p> : null}
      {erro ? (
        <p className="text-sm text-destructive" role="alert">
          {erro}
        </p>
      ) : null}

      {!carregando && !erro && leads.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum contato recebido ainda.</p>
      ) : null}

      <ul className="grid gap-4">
        {leads.map((lead) => (
          <li key={lead.id} className="rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-foreground">{lead.nome}</p>
              <p className="text-xs text-muted-foreground">{formatoData.format(new Date(lead.criado_em))}</p>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <a className="underline-offset-2 hover:underline" href={`tel:${lead.telefone.replace(/\D/g, "")}`}>
                {lead.telefone}
              </a>
              <a className="underline-offset-2 hover:underline" href={`mailto:${lead.email}`}>
                {lead.email}
              </a>
              <span>Origem: {lead.origem}</span>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground">{lead.assunto}</p>
            {lead.mensagem ? <p className="mt-1 text-sm text-foreground">{lead.mensagem}</p> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
